import { useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import api from '../lib/api';

/**
 * Read-only or inline-edit for plate / label fields during tests or repairs.
 * Persists via PUT /api/assets/:id (org-scoped).
 *
 * Optional ref: { saveIfNeeded() } — persists when in edit mode (e.g. before recording a repair).
 */
export const AssetDetailsEditBlock = forwardRef(function AssetDetailsEditBlock(
  { asset, onUpdated, startInEditMode = false, compact = false },
  ref
) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [make, setMake] = useState('');
  const [model, setModel] = useState('');
  const [equipmentCode, setEquipmentCode] = useState('');
  const [dbNumber, setDbNumber] = useState('');
  const [circuitBreakerNumber, setCircuitBreakerNumber] = useState('');
  const [capacity, setCapacity] = useState('');
  const [building, setBuilding] = useState('');
  const [room, setRoom] = useState('');

  const isBoardOrRcd =
    asset?.asset_type === 'rcd' || asset?.asset_type === 'distribution_board';

  const updateMutation = useMutation(
    (payload) => api.put(`/assets/${asset.id}`, payload).then((res) => res.data.asset),
    {
      onSuccess: (updated) => {
        queryClient.invalidateQueries(['assets']);
        queryClient.invalidateQueries(['asset', asset.id]);
        toast.success('Asset details updated');
        setEditing(false);
        onUpdated?.(updated);
      },
      onError: (err) => {
        const msg = err.response?.data?.error || err.response?.data?.message || 'Could not update asset';
        toast.error(msg);
      },
    }
  );

  /* Switching to another asset loads fields; edit mode follows startInEditMode */
  useEffect(() => {
    if (!asset) return;
    setMake(asset.manufacturer ?? '');
    setModel(asset.model ?? '');
    setEquipmentCode(asset.equipment_code ?? '');
    setDbNumber(asset.db_number ?? '');
    setCircuitBreakerNumber(asset.circuit_breaker_number ?? '');
    setCapacity(asset.capacity ?? '');
    setBuilding(asset.building ?? '');
    setRoom(asset.room ?? '');
    setEditing(!!startInEditMode);
  }, [asset?.id, startInEditMode]);

  useImperativeHandle(
    ref,
    () => ({
      async saveIfNeeded() {
        if (!asset || !editing) return;
        await updateMutation.mutateAsync({
          manufacturer: make.trim(),
          model: model.trim(),
          equipmentCode: equipmentCode.trim(),
          dbNumber: dbNumber.trim(),
          circuitBreakerNumber: circuitBreakerNumber.trim(),
          capacity: capacity.trim(),
          building: building.trim(),
          room: room.trim(),
        });
      },
    }),
    [asset, editing, make, model, equipmentCode, dbNumber, circuitBreakerNumber, capacity, building, room, updateMutation]
  );

  /* Same row refreshed from parent while not in edit mode */
  useEffect(() => {
    if (!asset || editing) return;
    setMake(asset.manufacturer ?? '');
    setModel(asset.model ?? '');
    setEquipmentCode(asset.equipment_code ?? '');
    setDbNumber(asset.db_number ?? '');
    setCircuitBreakerNumber(asset.circuit_breaker_number ?? '');
    setCapacity(asset.capacity ?? '');
    setBuilding(asset.building ?? '');
    setRoom(asset.room ?? '');
  }, [
    editing,
    asset?.id,
    asset?.manufacturer,
    asset?.model,
    asset?.equipment_code,
    asset?.db_number,
    asset?.circuit_breaker_number,
    asset?.capacity,
    asset?.building,
    asset?.room,
  ]);

  if (!asset) return null;

  const resetFromAsset = () => {
    setMake(asset.manufacturer ?? '');
    setModel(asset.model ?? '');
    setEquipmentCode(asset.equipment_code ?? '');
    setDbNumber(asset.db_number ?? '');
    setCircuitBreakerNumber(asset.circuit_breaker_number ?? '');
    setCapacity(asset.capacity ?? '');
    setBuilding(asset.building ?? '');
    setRoom(asset.room ?? '');
  };

  const handleSave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    updateMutation.mutate({
      manufacturer: make.trim(),
      model: model.trim(),
      equipmentCode: equipmentCode.trim(),
      dbNumber: dbNumber.trim(),
      circuitBreakerNumber: circuitBreakerNumber.trim(),
      capacity: capacity.trim(),
      building: building.trim(),
      room: room.trim(),
    });
  };

  const handleCancel = () => {
    resetFromAsset();
    setEditing(false);
  };

  const display = (v) => (v != null && String(v).trim() !== '' ? String(v).trim() : '—');

  return (
    <div className={`asset-details-edit-block ${compact ? 'asset-details-edit-block-compact' : ''}`.trim()}>
      {compact ? (
        !editing ? (
          <div className="asset-details-edit-compact-shell">
            <button
              type="button"
              className="btn btn-sm btn-secondary asset-details-edit-compact-edit-btn"
              onClick={() => setEditing(true)}
            >
              Edit
            </button>
            <dl className="asset-details-edit-dl asset-details-edit-dl-compact">
              <div>
                <dt>Building</dt>
                <dd>{display(asset.building)}</dd>
              </div>
              <div>
                <dt>Room</dt>
                <dd>{display(asset.room)}</dd>
              </div>
              <div>
                <dt>Make</dt>
                <dd>{display(asset.manufacturer)}</dd>
              </div>
              <div>
                <dt>Model</dt>
                <dd>{display(asset.model)}</dd>
              </div>
              {isBoardOrRcd ? (
                <>
                  <div>
                    <dt>DB Number</dt>
                    <dd>{display(asset.db_number)}</dd>
                  </div>
                  <div>
                    <dt>Circuit Breaker</dt>
                    <dd>{display(asset.circuit_breaker_number)}</dd>
                  </div>
                </>
              ) : (
                <div>
                  <dt>Equipment code</dt>
                  <dd>{display(asset.equipment_code)}</dd>
                </div>
              )}
              <div>
                <dt>Capacity</dt>
                <dd>{display(asset.capacity)}</dd>
              </div>
            </dl>
          </div>
        ) : null
      ) : (
        <div className="asset-details-edit-block-header">
          <span className="form-label-block" style={{ marginBottom: 0 }}>
            Asset details
          </span>
          {!editing ? (
            <button
              type="button"
              className="btn btn-sm btn-secondary"
              onClick={() => setEditing(true)}
            >
              Edit
            </button>
          ) : null}
        </div>
      )}

      {!editing ? (
        !compact ? (
          <dl className="asset-details-edit-dl">
            <div>
              <dt>Building</dt>
              <dd>{display(asset.building)}</dd>
            </div>
            <div>
              <dt>Room</dt>
              <dd>{display(asset.room)}</dd>
            </div>
            <div>
              <dt>Make</dt>
              <dd>{display(asset.manufacturer)}</dd>
            </div>
            <div>
              <dt>Model</dt>
              <dd>{display(asset.model)}</dd>
            </div>
            {isBoardOrRcd ? (
              <>
                <div>
                  <dt>DB Number</dt>
                  <dd>{display(asset.db_number)}</dd>
                </div>
                <div>
                  <dt>Circuit Breaker</dt>
                  <dd>{display(asset.circuit_breaker_number)}</dd>
                </div>
              </>
            ) : (
              <div>
                <dt>Equipment code</dt>
                <dd>{display(asset.equipment_code)}</dd>
              </div>
            )}
            <div>
              <dt>Capacity</dt>
              <dd>{display(asset.capacity)}</dd>
            </div>
          </dl>
        ) : null
      ) : (
        <div className="asset-details-edit-form">
          <div className="form-group">
            <label htmlFor={`asset-building-${asset.id}`}>Building</label>
            <input
              id={`asset-building-${asset.id}`}
              type="text"
              value={building}
              onChange={(e) => setBuilding(e.target.value)}
              autoComplete="off"
              disabled={updateMutation.isLoading}
            />
          </div>
          <div className="form-group">
            <label htmlFor={`asset-room-${asset.id}`}>Room</label>
            <input
              id={`asset-room-${asset.id}`}
              type="text"
              value={room}
              onChange={(e) => setRoom(e.target.value)}
              autoComplete="off"
              disabled={updateMutation.isLoading}
            />
          </div>
          <div className="form-group">
            <label htmlFor={`asset-make-${asset.id}`}>Make</label>
            <input
              id={`asset-make-${asset.id}`}
              type="text"
              value={make}
              onChange={(e) => setMake(e.target.value)}
              autoComplete="off"
              disabled={updateMutation.isLoading}
            />
          </div>
          <div className="form-group">
            <label htmlFor={`asset-model-${asset.id}`}>Model</label>
            <input
              id={`asset-model-${asset.id}`}
              type="text"
              value={model}
              onChange={(e) => setModel(e.target.value)}
              autoComplete="off"
              disabled={updateMutation.isLoading}
            />
          </div>
          {isBoardOrRcd ? (
            <>
              <div className="form-group">
                <label htmlFor={`asset-db-${asset.id}`}>DB Number</label>
                <input
                  id={`asset-db-${asset.id}`}
                  type="text"
                  value={dbNumber}
                  onChange={(e) => setDbNumber(e.target.value)}
                  autoComplete="off"
                  disabled={updateMutation.isLoading}
                />
              </div>
              <div className="form-group">
                <label htmlFor={`asset-cb-${asset.id}`}>Circuit Breaker Number</label>
                <input
                  id={`asset-cb-${asset.id}`}
                  type="text"
                  value={circuitBreakerNumber}
                  onChange={(e) => setCircuitBreakerNumber(e.target.value)}
                  autoComplete="off"
                  disabled={updateMutation.isLoading}
                />
              </div>
            </>
          ) : (
            <div className="form-group">
              <label htmlFor={`asset-eq-${asset.id}`}>Equipment code</label>
              <input
                id={`asset-eq-${asset.id}`}
                type="text"
                value={equipmentCode}
                onChange={(e) => setEquipmentCode(e.target.value)}
                autoComplete="off"
                disabled={updateMutation.isLoading}
              />
            </div>
          )}
          <div className="form-group">
            <label htmlFor={`asset-cap-${asset.id}`}>Capacity</label>
            <input
              id={`asset-cap-${asset.id}`}
              type="text"
              value={capacity}
              onChange={(e) => setCapacity(e.target.value)}
              autoComplete="off"
              placeholder="e.g. rating / size"
              disabled={updateMutation.isLoading}
            />
          </div>
          <div className="asset-details-edit-actions">
            <button
              type="button"
              className="btn btn-sm"
              onClick={handleCancel}
              disabled={updateMutation.isLoading}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-sm btn-primary"
              onClick={handleSave}
              disabled={updateMutation.isLoading}
            >
              {updateMutation.isLoading ? 'Saving…' : 'Save details'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
});

AssetDetailsEditBlock.displayName = 'AssetDetailsEditBlock';
